// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 19-design-system.md

import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

/**
 * Where `/auth/check` sends a signed-in user who is refused an app.
 *
 * Until this existed the redirect landed on the router's `*` catch-all, so
 * someone who simply lacked access was told the page did not exist — a wrong
 * answer that also hides the one thing they can do about it. The distinction
 * this page has to carry is "you are signed in, this app is real, it is not
 * open to you", which is a different sentence from both 404 and 401.
 *
 * The only way to reach it is a restricted app the viewer holds no grant for, and
 * access to one is granted by its owner, by email. So naming that person is the
 * single action the page can offer, and it does not point anywhere else: a
 * restricted app is withheld from the marketplace catalog by design.
 */
/**
 * Everything that depends on looking the app up: its display name, and the owner
 * to ask.
 *
 * Its own component so the read is only mounted when there is a slug to look up
 * — the alternative is a disabled query, and a hook that sometimes does nothing
 * is harder to read than a child that sometimes is not there.
 */
function RefusedApp({ slug }: { readonly slug: string }): React.JSX.Element {
  const contact = api.apps.ownerContact.useQuery(['app-owner-contact', slug], {
    params: { slug },
  });
  const owner = contact.data?.status === 200 ? contact.data.body : undefined;

  return (
    <>
      {/* The display name is what the reader recognises; the slug is what the
          redirect carried, and stands in until the name arrives. */}
      <Refusal name={owner?.app_name ?? slug} />
      {contact.isPending ? (
        <div className="flex justify-center">
          <Skeleton className="h-24 w-full max-w-sm" />
        </div>
      ) : owner === undefined ? (
        // A failed or refused lookup says the honest minimum rather than
        // inventing a name: the reader still learns an owner is who they need.
        <AskTheOwner />
      ) : (
        <div className="rounded-gl-lg border border-border bg-card p-4 text-left">
          <Eyebrow>Ask for access</Eyebrow>
          <p className="mt-2 text-sm leading-relaxed text-foreground">
            You can contact the owner to request access:{' '}
            <span className="font-medium">{owner.owner_name ?? owner.owner_email}</span>.
          </p>
          <a
            className="mt-1 inline-block text-sm font-medium text-link underline underline-offset-2"
            href={`mailto:${owner.owner_email}?subject=${encodeURIComponent(
              `Access to ${owner.app_name}`,
            )}`}
          >
            {owner.owner_email}
          </a>
        </div>
      )}
    </>
  );
}

function Refusal({ name }: { readonly name: string | null }): React.JSX.Element {
  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      {name !== null ? (
        <>
          You&rsquo;re signed in, but <span className="font-medium text-foreground">{name}</span> is
          limited to specific people and you aren&rsquo;t one of them.
        </>
      ) : (
        <>
          You&rsquo;re signed in, but this app is limited to specific people and you aren&rsquo;t
          one of them.
        </>
      )}
    </p>
  );
}

function AskTheOwner(): React.JSX.Element {
  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      Ask the app&rsquo;s owner to give you access.
    </p>
  );
}

export function AccessDeniedPage(): React.JSX.Element {
  usePageTitle('Access denied');
  const [params] = useSearchParams();
  const slug = params.get('app');

  return (
    <div className="mx-auto max-w-lg space-y-6 py-16 text-center" data-testid="access-denied">
      <div className="flex justify-center">
        <span className="rounded-full bg-muted p-3">
          <Lock className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        </span>
      </div>

      <h1 className="text-2xl font-semibold tracking-[-0.02em]">You don&rsquo;t have access</h1>

      {slug !== null ? (
        <RefusedApp slug={slug} />
      ) : (
        <>
          <Refusal name={null} />
          <AskTheOwner />
        </>
      )}

      <div className="flex justify-center">
        <Button asChild variant="outline" className="h-11 md:h-9">
          <Link to="/apps">Go to your apps</Link>
        </Button>
      </div>
    </div>
  );
}
